import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown } from 'react-feather';
import { Dialog } from '@/shared/components/Dialog';
import { Dropdown } from '@/shared/components/Dropdown';
import { TextField } from '@/shared/components/TextField';
import { Checkbox } from '@/shared/components/Checkbox/Checkbox';
import type { EligibleAssignee } from '../../types/assignContactRequestTypes';
import type { AssignStep } from '../../hooks/useAssignContactRequest';
import { CONTACT_REQUEST_LABELS as L } from '../../constants/labels';
import { CONTACT_REQUEST_LABELS as TL } from '../../constants/translations/labels';

/** Route for creating a new admin, linked from the "No admins available" message. */
const ADD_ADMIN_ROUTE = '/administrators/addUser';

interface AssignContactRequestModalProps {
  open: boolean;
  onClose: () => void;
  assignees: EligibleAssignee[];
  isLoadingAssignees: boolean;
  isLoadingMoreAssignees: boolean;
  hasMoreAssignees: boolean;
  onLoadMoreAssignees: () => void;
  assigneesError: string;
  assigneeSearch: string;
  onAssigneeSearchChange: (value: string) => void;
  selectedAssigneeId: string;
  onSelectAssignee: (id: string) => void;
  /** The assignee the modal was opened with (e.g. the current assignee on reassignment). */
  initialSelectedAssignee?: EligibleAssignee | null;
  selectedAssigneeError: string;
  isSubmitting: boolean;
  submitError: string;
  onSubmit: () => void;
  canSubmit: boolean;
  /**
   * Reassignment (request already had an assignee): switches the title, adds a
   * confirmation step, and shows the email-notify toggle. When false the modal
   * is the original single-step assign flow.
   */
  isReassign?: boolean;
  /** Which step to render: the picker (`select`) or the review (`confirm`). */
  step?: AssignStep;
  /** Advance from the picker to the confirmation step. */
  onContinue?: () => void;
  /** Return from the confirmation step to the picker. */
  onBack?: () => void;
  /** Whether the newly assigned admin will be emailed (reassign flow only). */
  notify?: boolean;
  onNotifyChange?: (value: boolean) => void;
  /** Client name shown in the confirmation-step assignment cards. */
  clientName?: string;
}

/** Single label/value pair inside a confirmation card. */
const CardField: React.FC<{ label: string; value: string }> = ({
  label,
  value,
}) => (
  <div className="flex flex-col gap-1 min-w-0">
    <span
      className="text-on-surface-variant"
      style={{ fontSize: 12, lineHeight: '16px', letterSpacing: 0.4 }}
    >
      {label}
    </span>
    <span
      className="text-neutral-10 break-words"
      style={{ fontSize: 16, lineHeight: '24px', fontWeight: 400 }}
    >
      {value || '—'}
    </span>
  </div>
);

/** Card summarising an assignment (assigned user + client), used on both steps. */
const AssignmentCard: React.FC<{
  heading: string;
  assigneeLabel: string;
  assigneeName: string;
  clientLabel: string;
  clientName: string;
  testId: string;
}> = ({
  heading,
  assigneeLabel,
  assigneeName,
  clientLabel,
  clientName,
  testId,
}) => (
  <div
    className="rounded-lg border border-[#DCDDE0] bg-white"
    style={{ padding: 16 }}
    data-testid={testId}
  >
    <h3
      className="text-on-surface-variant"
      style={{
        fontSize: 18,
        lineHeight: '24px',
        fontWeight: 500,
        letterSpacing: 0.1,
        marginBottom: 16,
      }}
    >
      {heading}
    </h3>
    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
      <CardField label={assigneeLabel} value={assigneeName} />
      <CardField label={clientLabel} value={clientName} />
    </div>
  </div>
);

/**
 * Modal dialog for assigning a contact request to an eligible admin.
 *
 * Data fetching, debounced search, and pagination all live upstream in
 * `useAssignContactRequest` (mirroring `EligibleTechnicianDropdown`'s
 * fetch-on-scroll pattern) — this component just renders the searchable
 * admin picker, a read-only "Admin ID" field that auto-fills from the
 * selection, and submit/cancel controls.
 */
const AssignContactRequestModal: React.FC<AssignContactRequestModalProps> = ({
  open,
  onClose,
  assignees,
  isLoadingAssignees,
  isLoadingMoreAssignees,
  hasMoreAssignees,
  onLoadMoreAssignees,
  assigneesError,
  assigneeSearch,
  onAssigneeSearchChange,
  selectedAssigneeId,
  onSelectAssignee,
  initialSelectedAssignee,
  selectedAssigneeError,
  isSubmitting,
  submitError,
  onSubmit,
  canSubmit,
  isReassign = false,
  step = 'select',
  onContinue,
  onBack,
  notify = false,
  onNotifyChange,
  clientName = '',
}) => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  // Cache the selected row so the trigger keeps showing its name even after
  // a new search/page refresh drops it from the currently loaded `assignees`.
  const [lastSelected, setLastSelected] = useState<EligibleAssignee | null>(
    null
  );
  const wasOpenRef = useRef(false);

  // Seed the trigger's display from the current assignee (if any) the moment
  // the modal opens, so it's visible before the fetched `assignees` page —
  // which may not include this admin — comes back.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setLastSelected(initialSelectedAssignee ?? null);
    }
    wasOpenRef.current = open;
  }, [open, initialSelectedAssignee]);

  useEffect(() => {
    if (!selectedAssigneeId) {
      setLastSelected(null);
      return;
    }
    const found = assignees.find((a) => a.id === selectedAssigneeId);
    if (found) {
      setLastSelected(found);
    }
  }, [assignees, selectedAssigneeId]);

  const selected =
    assignees.find((a) => a.id === selectedAssigneeId) ??
    (selectedAssigneeId && lastSelected?.id === selectedAssigneeId
      ? lastSelected
      : null);

  const displayError = isDropdownOpen ? '' : selectedAssigneeError;
  const showNoAdminsMessage =
    !isLoadingAssignees &&
    !assigneesError &&
    !assigneeSearch.trim() &&
    assignees.length === 0;

  const items = isLoadingAssignees
    ? [{ id: '__loading__', label: L.ASSIGN_CR_LOADING, disabled: true }]
    : assigneesError && assignees.length === 0
      ? [{ id: '__error__', label: assigneesError, disabled: true }]
      : assignees.length > 0
        ? [
            ...assignees.map((a) => ({
              id: a.id,
              label: `${a.name} [${a.user_id}]`,
              toolTip: false as const,
            })),
            ...(isLoadingMoreAssignees
              ? [
                  {
                    id: '__loading_more__',
                    label: L.ASSIGN_CR_LOADING,
                    disabled: true,
                  },
                ]
              : []),
          ]
        : [{ id: '__empty__', label: L.ASSIGN_CR_NO_RESULTS, disabled: true }];

  const NON_SELECTABLE_IDS = [
    '__loading__',
    '__error__',
    '__empty__',
    '__loading_more__',
  ];

  const handleItemClick = (id: string) => {
    if (!NON_SELECTABLE_IDS.includes(id)) {
      onSelectAssignee(id);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={700}
      closeOnOverlayClick={!isSubmitting}
      closeOnEscape={!isSubmitting}
      className="overflow-visible!"
      data-testid="assign-cr-modal"
    >
      <div
        className="flex flex-col relative"
        style={{ maxHeight: 'calc(100vh - 48px)' }}
      >
        <div className="px-6 pt-6 shrink-0">
          <h2
            className="text-neutral-10 font-normal"
            style={{
              fontSize: 22,
              lineHeight: '28px',
              marginBottom: step !== 'confirm' ? 8 : 0,
            }}
            data-testid="assign-cr-title"
          >
            {step === 'confirm'
              ? L.ASSIGN_CR_CONFIRM_TITLE
              : isReassign
                ? L.ASSIGN_CR_REASSIGN_MODAL_TITLE
                : L.ASSIGN_CR_MODAL_TITLE}
          </h2>
        </div>

        <div className="px-6 pt-2 flex flex-col gap-2">
          {step === 'select' && (
            <>
              {isReassign && (
                <AssignmentCard
                  heading={L.ASSIGN_CR_REASSIGN_SECTION_CURRENT}
                  assigneeLabel={L.ASSIGN_CR_REASSIGN_FIELD_ASSIGNED_USER}
                  assigneeName={initialSelectedAssignee?.name ?? ''}
                  clientLabel={L.ASSIGN_CR_REASSIGN_FIELD_CLIENT_NAME}
                  clientName={clientName}
                  testId="assign-cr-select-current"
                />
              )}
              <p
                className="text-[18px] font-normal text-on-surface-variant"
                style={{ lineHeight: '26px', marginTop: isReassign ? 16 : 0 }}
              >
                {isReassign
                  ? L.ASSIGN_CR_REASSIGN_SECTION_NEW
                  : L.ASSIGN_CR_SECTION_TITLE}
              </p>

              <div className="flex flex-col gap-2">
                <div className="flex gap-4">
                  <div className="flex-1 min-w-0" data-field="assign-cr-admin">
                    <Dropdown
                      menuFullWidth
                      toolTip={false}
                      showSearch
                      searchPlaceholder={L.ASSIGN_CR_SEARCH_PLACEHOLDER}
                      menuMaxVisibleItems={5}
                      autoMaxHeight
                      disabled={isSubmitting}
                      open={isDropdownOpen}
                      onOpenChange={setIsDropdownOpen}
                      onSearchChange={onAssigneeSearchChange}
                      items={items}
                      {...(selectedAssigneeId
                        ? { selectedId: selectedAssigneeId }
                        : {})}
                      onItemClick={handleItemClick}
                      onScrollEnd={() => {
                        if (hasMoreAssignees && !isLoadingMoreAssignees) {
                          onLoadMoreAssignees();
                        }
                      }}
                      error={Boolean(displayError)}
                      errorMessage={displayError}
                      trigger={({
                        open: isOpen,
                        toggle,
                        disabled,
                        error: triggerError,
                      }) => {
                        const labelFloating = isOpen || !!selected;
                        const labelColor = isOpen ? '#2a4455' : '#474A4C';
                        return (
                          <button
                            type="button"
                            onClick={disabled ? undefined : toggle}
                            aria-expanded={isOpen}
                            aria-haspopup="true"
                            aria-disabled={disabled}
                            data-testid="assign-cr-admin-trigger"
                            className={`w-full flex items-center justify-between gap-2 bg-transparent outline-none text-left ${
                              disabled
                                ? 'cursor-not-allowed text-neutral-60'
                                : 'cursor-pointer text-neutral-10'
                            }`}
                          >
                            <div
                              className="flex-1 min-w-0 relative flex flex-col justify-center"
                              style={{ minHeight: 48 }}
                            >
                              <span
                                className="absolute left-0 pointer-events-none origin-top-left font-normal"
                                style={{
                                  color: labelColor,
                                  top: labelFloating ? 0 : 14,
                                  fontSize: labelFloating ? 12 : 16,
                                  lineHeight: labelFloating ? '16px' : '20px',
                                  transition:
                                    'top 200ms ease-out, font-size 200ms ease-out, color 200ms ease-out',
                                }}
                              >
                                {isReassign
                                  ? L.ASSIGN_CR_REASSIGN_FIELD_ADMIN_NAME
                                  : L.ASSIGN_CR_FIELD_ADMIN_NAME}
                              </span>
                              {selected && (
                                <span
                                  className="text-neutral-10 block w-full truncate"
                                  style={{
                                    marginTop: 16,
                                    fontSize: 14,
                                    lineHeight: '20px',
                                    fontWeight: 400,
                                  }}
                                >
                                  {selected.name}
                                </span>
                              )}
                            </div>
                            <ChevronDown
                              strokeWidth={1.5}
                              className={`shrink-0 transition-transform duration-200 ${
                                isOpen ? 'rotate-180' : ''
                              } ${triggerError ? 'text-error-40' : ''}`}
                            />
                          </button>
                        );
                      }}
                    />
                  </div>

                  {/* Admin ID — read-only, shown only once an admin has been selected */}
                  <div className="flex-1 min-w-0">
                    {selected && (
                      <TextField
                        fullWidth
                        label={TL.ADMIN_ID_LABEL.polish}
                        value={selected.user_id}
                        disabled
                        dataTestId="assign-cr-admin-id-value"
                      />
                    )}
                  </div>
                </div>

                {showNoAdminsMessage && (
                  <p
                    className="text-[12px] leading-4"
                    style={{ color: '#474A4C' }}
                    data-testid="assign-cr-no-admins"
                  >
                    {L.ASSIGN_CR_NO_ADMINS}{' '}
                    <button
                      type="button"
                      className="underline font-medium cursor-pointer"
                      style={{ color: '#2A4455' }}
                      onClick={() => navigate(ADD_ADMIN_ROUTE)}
                      data-testid="assign-cr-add-admin-link"
                    >
                      {L.ASSIGN_CR_ADD_ADMIN_LINK}
                    </button>
                  </p>
                )}
              </div>
            </>
          )}

          {step === 'confirm' && (
            <div
              className="flex flex-col gap-3"
              data-testid="assign-cr-confirm-step"
            >
              <p className="text-[14px] leading-5 tracking-[0.25px] text-on-surface-variant mb-3">
                {L.ASSIGN_CR_CONFIRM_SUBTITLE}
              </p>

              <Checkbox
                checked={notify}
                onChange={(value) => onNotifyChange?.(value)}
                label={L.ASSIGN_CR_NOTIFY_LABEL}
                testId="assign-cr-notify"
              />

              <div className="flex flex-col gap-6">
                <AssignmentCard
                  heading={L.ASSIGN_CR_CONFIRM_SECTION_CURRENT}
                  assigneeLabel={L.ASSIGN_CR_FIELD_ASSIGNED_USER}
                  assigneeName={initialSelectedAssignee?.name ?? ''}
                  clientLabel={L.ASSIGN_CR_FIELD_CLIENT_NAME}
                  clientName={clientName}
                  testId="assign-cr-confirm-previous"
                />
                <AssignmentCard
                  heading={L.ASSIGN_CR_CONFIRM_SECTION_UPDATED}
                  assigneeLabel={L.ASSIGN_CR_FIELD_ASSIGNED_USER}
                  assigneeName={selected?.name ?? ''}
                  clientLabel={L.ASSIGN_CR_FIELD_CLIENT_NAME}
                  clientName={clientName}
                  testId="assign-cr-confirm-new"
                />
              </div>
            </div>
          )}

          {submitError && (
            <p
              className="text-error-40"
              style={{ fontSize: 12, lineHeight: '16px' }}
              data-testid="assign-cr-submit-error"
            >
              {submitError}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-7 shrink-0">
          {/* Left button: Cancel on the picker step, Back on the confirm step. */}
          <button
            type="button"
            onClick={step === 'confirm' ? onBack : onClose}
            disabled={isSubmitting}
            className={`h-10 px-5 rounded-lg border border-[#DCDDE0] bg-white text-[#181A1B] text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
              isSubmitting
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:bg-[#F3F4F6] cursor-pointer'
            }`}
            data-testid={
              step === 'confirm' ? 'assign-cr-back' : 'assign-cr-cancel'
            }
          >
            {step === 'confirm'
              ? L.ASSIGN_CR_BUTTON_BACK
              : L.ASSIGN_CR_BUTTON_CANCEL}
          </button>
          {/*
            Right button. Reassign picker step advances to the confirm step;
            confirm step submits. Plain assign has a single step and submits.
          */}
          {isReassign && step === 'select' ? (
            <button
              type="button"
              onClick={onContinue}
              className="h-10 px-5 rounded-lg bg-[#2A4455] text-white text-sm font-medium leading-5 tracking-[0.1px] transition-colors hover:bg-[#1f3340] cursor-pointer"
              data-testid="assign-cr-continue"
            >
              {L.ASSIGN_CR_BUTTON_CONTINUE}
            </button>
          ) : (
            <button
              type="button"
              onClick={onSubmit}
              disabled={!canSubmit}
              className={`h-10 px-5 rounded-lg bg-[#2A4455] text-white text-sm font-medium leading-5 tracking-[0.1px] transition-colors ${
                canSubmit
                  ? 'hover:bg-[#1f3340] cursor-pointer'
                  : 'opacity-50 cursor-not-allowed'
              }`}
              data-testid="assign-cr-submit"
            >
              {isReassign
                ? isSubmitting
                  ? L.ASSIGN_CR_BUTTON_CONFIRMING
                  : L.ASSIGN_CR_BUTTON_CONFIRM
                : isSubmitting
                  ? L.ASSIGN_CR_BUTTON_SUBMITTING
                  : L.ASSIGN_CR_BUTTON_SUBMIT}
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
};

export default AssignContactRequestModal;
